import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import {
  vdjColor, vdjBeatChroma, vdjLoop, vdjCameraKick, VDJ_MIRROR_CHUNK, VDJ_MIRROR_NAMES,
  VDJ_DROP_FLASH, VdjPost, VdjComfort
} from './lib/vdjLook.js';

// Infinity - a hall of mirrors. A few neon bars and rings float in a box
// whose six walls are mirrors, multiplying them to infinity in every
// direction; the camera is at the centre of the box. Melodic techno,
// progressive house, trance, psytrance. Applies the LOOK VDJ
// (lib/vdjLook.js). (The September instanced-lattice version is kept beside
// this file as infinity.js.avant-20261013.)
//
// THE FOLD. A box of mirrors is exactly a space tiled with mirrored copies
// of the box. Fold the march point back into the one real box,
//     q = abs(mod(p - L, 4L) - 2L) - L        (L: the box's half-size)
// and measure the neons there: one scene, every reflection of every
// reflection, as true MIRROR images. (mod(p + L, ...) would map the real box
// onto its own mirror image; mod(p - L, ...) keeps cell 0 the identity.)
//
// WHAT THE MARCH STORES - and why the result is sharp. The march does not
// produce a colour. Per pixel of a half-resolution HalfFloat target it keeps
// the ray's CLOSEST APPROACH to any neon:
//     R = that distance (to the tube's centreline), G = the depth along the
//     ray where it happens, B = which neon (6 = the box's edges), A = how
//     many mirrors the ray had crossed by then.
// Distances interpolate correctly under the bilinear upscale; colours do
// not (the first version painted soft glow at half resolution and the
// upscale smeared it, then the bloom smeared it again). The composite then
// runs at FULL resolution and turns the distance into PIXELS at that depth:
// a white core of constant width (2 px radius) and a 6 px halo in the neon's
// colour, kept under the bloom threshold so the bloom only adds a rim.
//
// The closest approach is found by sphere tracing with t += max(d, 0.02 L),
// refined between two samples on the same neon by the exact closest
// approach of a line, d(t) = sqrt(r^2 + (t - t0)^2) - the floor step alone
// would miss the true minimum by up to 0.01 L, about eight pixels at one box.

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'infinity',
  family: 'shader',
  danceability: 0.86,
  requiresTier: 1,
  energyLevel: 0.80,
  bpmMin: 118,
  bpmMax: 150,
  movement: 0.75,
  tonality: 0.50,
  enabled: true
};

// scale: the march target's fraction of the drawing buffer. Measured on the
// Pi (1023x320 buffer, visualizer alone, closest-approach renderer): the
// brief's tier 1 (a third, 24 steps) costs 46 ms; a fifth with 18 steps
// 19.5 ms (20 steps: 21.6) - about 30.5 ms with the ~11 ms composer, the
// 30 fps criterion. At a fifth, small octahedra read as filled diamonds.
// Tier 2 (200 ms there) is for the TV and desktop GPUs.
const TIER_LAYOUT = {
  2: { scale: 0.5, steps: 32, neons: 6, stride: 1.0 },
  1: { scale: 0.2, steps: 18, neons: 4, stride: 1.0 }
};
const DEFAULT_TIER = 2;
const NEON_SLOTS = 6;                 // uniform arrays in the composite
const EDGE_INDEX = 6;

// --- the box and the neons ------------------------------------------------------
const L0 = 1.0;                       // half-size of the box, world units
const BUILD_L = 0.62;                 // of L0 at the top of a build
const BUILD_EASE_BARS = 8;
const MIRROR_ATT = 0.85;              // per mirror crossed
const ACCENT_TINT = 0.6;
const FOG_FROM_BOXES = 9, FOG_TO_BOXES = 12;
const STEP_FLOOR = 0.02;              // of L
const T_START = 0.02;                 // of L
// Small neons well away from the camera at the centre: a bar 1.5 L long at
// half a box from the lens filled the frame, and an octahedron of 0.22 L at
// 0.62 L still took half of it. Sizes ~0.12-0.18 L at 0.65-0.85 L: about a
// third of the height at most for the nearest, under a sixth of the area.
const BAR_HALF = 0.18;
const RING_R = 0.12, OCTA_R = 0.13;
const NEON_DIST_MIN = 0.65, NEON_DIST_MAX = 0.85;
// The tube's world radius. It sets the core's width in pixels at depth t,
// CAPPED at CORE_PX: the near neons get the brief's constant 4 px core, the
// copies further back thin out with distance instead of turning into solid
// 4 px blobs (a far octahedron's edges merged into a filled square).
const TUBE_R = 0.006;                 // of L
const EDGE_R = 0.003;                 // of L
const CULL = 0.25;                    // of L: beyond this a neon is skipped

// --- the composite -------------------------------------------------------------
const CORE_PX = 2.0;                  // radius: a 4 px core
const EDGE_CORE_PX = 0.6;
const HALO_PX = 6.0;
const HALO_GAIN = 0.35;
const EDGE_GAIN = 0.30;               // the edges: accent at 30%
const WARM_WHITE = [1.0, 0.94, 0.86];
const ROT_PERIOD_BEATS = 16;
const PULSE = 0.5;                    // the beat: every copy flashes together
const PULSE_TAU_S = 0.11;
const FLICKER = 0.30;                 // bands.high

// --- the camera -----------------------------------------------------------------
const FOV_DEG = 60;
const DRIFT_PERIOD_BEATS = 16;
const DRIFT_YAW = 0.30, DRIFT_PITCH = 0.14;
const FLY_BOXES_PER_BEAT = 0.5;
const DROP_FLY_BOXES_PER_BEAT = 1.6;
const DROP_BARS = 4;
const FLY_CHANCE = 0.3;
const MIRROR_MODES = ['none', 'none', 'h', 'quad', 'kaleido'];
const VIEWS = [
  [0, 0, -1], [0, 1, -0.15], [1, 1, -1], [0.35, -0.2, -1], [-1, 0.35, -0.6], [0.6, -1, -0.2], [1, 0.05, 0]
];

const SAFE_FLOOR = 0.5;
const FREE_BPM = 128;
const LUM_W = 128, LUM_H = 72;
const LUM_EVERY = 12;

const _m4 = new THREE.Matrix4();
const _v0 = new THREE.Vector3();
const _v1 = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
const _size = new THREE.Vector2();
const _q = new THREE.Quaternion();

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}

// ---------------------------------------------------------------------------
// Shaders
// ---------------------------------------------------------------------------

const quadVertexShader = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

// stride: the step is max(stride x d, floor). Kept at 1: over-relaxing
// (tried 1.3 / 2.0) breaks the line refinement - between two samples the
// nearest COPY of an edge changes from one wall to the next under the same
// index, and the model then invents near-zero minima all over the frame.
function marchFragmentShader(steps, neons, stride) {
  return `
  precision highp float;

  #define STEPS ${steps}
  #define NEONS ${neons}

  uniform float uAspect;
  uniform vec3 uCamPos;
  uniform vec3 uCamCell;
  uniform mat3 uCamRot;
  uniform float uTanHalf;
  uniform float uRoll;
  uniform float uL;
  uniform float uFly;
  uniform float uNeonType[NEONS];
  uniform vec3 uNeonPos[NEONS];
  uniform mat3 uNeonRot[NEONS];
  uniform float uNeonRad[NEONS];

  varying vec2 vUv;

  float segD(vec3 p, vec3 a, vec3 b) {
    vec3 pa = p - a, ba = b - a;
    float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
    return length(pa - ba * h);
  }

  // Distance to the nearest centreline in the folded box, and which one.
  // exact: 0 when the nearest is only a bounding-sphere bound (a culled
  // neon), whose distance does NOT follow the line model of the refinement.
  float sceneD(vec3 q, out float idx, out float exact) {
    float L = uL;
    // The box's twelve edges: the two smallest distances to the walls.
    vec3 e = L - abs(q);
    float m1 = min(e.x, min(e.y, e.z));
    float m3 = max(e.x, max(e.y, e.z));
    float m2 = e.x + e.y + e.z - m1 - m3;
    float d = length(vec2(m1, m2));
    idx = ${EDGE_INDEX}.0;
    exact = 1.0;
    for (int n = 0; n < NEONS; n++) {
      vec3 rel = q - uNeonPos[n];
      float bound = length(rel) - uNeonRad[n];
      if (bound > ${CULL.toFixed(2)} * L) {
        if (bound < d) { d = bound; idx = float(n); exact = 0.0; }
        continue;
      }
      vec3 l = rel * uNeonRot[n];
      float ty = uNeonType[n];
      float dn;
      if (ty < 0.5) {
        dn = length(l - vec3(clamp(l.x, -${BAR_HALF.toFixed(2)} * L, ${BAR_HALF.toFixed(2)} * L), 0.0, 0.0));
      } else if (ty < 1.5) {
        dn = length(vec2(length(l.xz) - ${RING_R.toFixed(2)} * L, l.y));
      } else {
        vec3 a = abs(l);
        float R = ${OCTA_R.toFixed(2)} * L;
        dn = min(segD(a, vec3(R, 0.0, 0.0), vec3(0.0, R, 0.0)),
             min(segD(a, vec3(0.0, R, 0.0), vec3(0.0, 0.0, R)),
                 segD(a, vec3(0.0, 0.0, R), vec3(R, 0.0, 0.0))));
      }
      if (dn < d) { d = dn; idx = float(n); exact = 1.0; }
    }
    return d;
  }

  void main() {
    vec2 s = vUv * 2.0 - 1.0;
    s.x *= uAspect;
    float cr = cos(uRoll), sr = sin(uRoll);
    s = vec2(s.x * cr - s.y * sr, s.x * sr + s.y * cr);
    vec3 rd = normalize(uCamRot * vec3(s * uTanHalf, -1.0));
    vec3 ro = uCamPos;
    float L = uL;

    float bestA = 1e9, bestD = 1e3, bestT = 1.0, bestI = ${EDGE_INDEX}.0;
    float t = ${T_START.toFixed(2)} * L;
    float dPrev = -1.0, tPrev = 0.0, iPrev = -1.0, ePrev = 0.0;
    for (int i = 0; i < STEPS; i++) {
      vec3 p = ro + rd * t;
      vec3 q = abs(mod(p - L, 4.0 * L) - 2.0 * L) - L;
      float idx, ex;
      float d = sceneD(q, idx, ex);
      // The sample itself, compared ANGULARLY (d / t): what matters is the
      // size on screen, not in the world.
      float a = d / t;
      if (a < bestA) { bestA = a; bestD = d; bestT = t; bestI = idx; }
      // The exact closest approach between this sample and the previous one
      // on the same centreline, treating it locally as a straight line.
      if (idx == iPrev && ex > 0.5 && ePrev > 0.5) {
        float sg = t - tPrev;
        float x = ((d * d - dPrev * dPrev) / sg - sg) * 0.5;   // tPrev - t0
        float t0 = tPrev - x;
        if (t0 > tPrev && t0 < t && abs(x) < sg) {
          float r = sqrt(max(dPrev * dPrev - x * x, 0.0));
          float a0 = r / t0;
          if (a0 < bestA) { bestA = a0; bestD = r; bestT = t0; bestI = idx; }
        }
      }
      dPrev = d; tPrev = t; iPrev = idx; ePrev = ex;
      t += max(d * ${stride.toFixed(2)}, ${STEP_FLOOR.toFixed(2)} * L);
    }
    // Mirrors crossed up to the closest approach: by cell while the camera
    // stays in its box, continuous while it flies through walls (a whole-cell
    // count pops at every crossing).
    vec3 pb = ro + rd * bestT;
    vec3 cell = floor((pb + L) / (2.0 * L));
    vec3 dc = abs(cell - uCamCell);
    vec3 dcs = abs(pb - ro) / (2.0 * L);
    float bounces = mix(dc.x + dc.y + dc.z, dcs.x + dcs.y + dcs.z, uFly);
    gl_FragColor = vec4(bestD / L, bestT / L, bestI, bounces);
  }
`;
}

// The composite, full resolution: the closest approach in PIXELS at its
// depth, a constant-width white core and a 6 px halo, the mirrors'
// attenuation, the fog, then the VDJ symmetry, the comfort gain, the safe
// zone and the drop flash. Also rendered into the small comfort target.
const compositeVertexShader = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.9999, 1.0); }
`;

const compositeFragmentShader = `
  precision highp float;

  uniform sampler2D uMarch;
  uniform vec2 uMarchSize;
  uniform float uL;
  uniform float uPxAngle;        // world size of one screen pixel at unit depth
  uniform float uScreenPerTexel; // screen pixels per march texel
  uniform vec3 uNeonCol[${NEON_SLOTS + 1}];
  uniform float uNeonGain[${NEON_SLOTS + 1}];
  uniform vec3 uAccent;
  uniform float uHaloPx;
  uniform float uMirror;
  uniform float uGain;
  uniform float uFlash;
  uniform float uDebug;          // 1: dmin in grey, 2: core only
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  varying vec2 vUv;

  ${VDJ_MIRROR_CHUNK}

  void main() {
    vec2 uv = vdjMirror(vUv, uMirror, uAspect);
    // Distances bilinear (they interpolate); the index and the bounce count
    // from the nearest texel (an interpolated index is another neon).
    vec4 m = texture2D(uMarch, uv);
    vec4 mn = texture2D(uMarch, (floor(uv * uMarchSize) + 0.5) / uMarchSize);
    float d = m.r * uL;
    float t = max(m.g * uL, 1e-3);
    float ps = t * uPxAngle;
    float dPx = d / ps;
    if (uDebug > 0.5 && uDebug < 1.5) {
      gl_FragColor = vec4(vec3(clamp(d / uL * 3.0, 0.0, 1.0)), 1.0);
      return;
    }
    float idx = floor(mn.b + 0.5);
    bool edge = idx > ${(EDGE_INDEX - 0.5).toFixed(1)};
    vec3 ncol = vec3(0.0);
    float gain = 1.0;
    for (int i = 0; i < ${NEON_SLOTS + 1}; i++) {
      if (abs(float(i) - idx) < 0.5) { ncol = uNeonCol[i]; gain = uNeonGain[i]; }
    }
    // Sub-pixel tubes: below one pixel of diameter the core stays one pixel
    // wide and dims in proportion - no crawling, no stair-steps in the back.
    float tubePx = (edge ? ${EDGE_R.toFixed(4)} : ${TUBE_R.toFixed(4)}) * uL / ps;
    float coreMax = edge ? ${EDGE_CORE_PX.toFixed(2)} : ${CORE_PX.toFixed(2)};
    float coreR = clamp(tubePx, 0.5, coreMax);
    float sub = clamp(tubePx / 0.5, 0.0, 1.0);
    // A distance in V interpolated between two march texels loses its
    // minimum: the centre of a line reads about half a texel away. The core
    // is never narrower than that, and dims in proportion (same energy) -
    // without it a 0.6 px edge vanished into dashes at tier 1's fifth res.
    float halfTexel = 0.5 * uScreenPerTexel;
    if (coreR < halfTexel) { sub *= coreR / halfTexel; coreR = halfTexel; }
    float core = 1.0 - smoothstep(coreR - 0.75, coreR + 0.75, dPx);
    // The halo shrinks with the core, so a distant copy keeps its shape.
    float haloPx = uHaloPx * clamp(coreR / ${CORE_PX.toFixed(2)}, 0.25, 1.0);
    float halo = exp(-dPx / haloPx) * ${HALO_GAIN.toFixed(2)};
    vec3 col;
    if (uDebug > 1.5) col = vec3(core);
    else if (edge) col = uAccent * (${EDGE_GAIN.toFixed(2)} * core);   // the grid: a line, no halo
    else col = vec3(${WARM_WHITE.map((v) => v.toFixed(2)).join(', ')}) * core + ncol * (core * 0.6 + halo);
    col *= sub * gain;
    // Each mirror crossed: 0.85, and a tint toward the accent.
    float att = exp2(mn.a * ${Math.log2(MIRROR_ATT).toFixed(5)});
    float lum = dot(col, vec3(0.3333));
    col = mix(col, uAccent * lum, (1.0 - att) * ${ACCENT_TINT.toFixed(2)}) * att;
    col *= 1.0 - smoothstep(${(2 * FOG_FROM_BOXES).toFixed(1)} * uL, ${(2 * FOG_TO_BOXES).toFixed(1)} * uL, t);
    col *= uGain;
    vec2 ndc = vUv * 2.0 - 1.0;
    vec2 sz = vec2(ndc.x * uAspect, ndc.y);
    vec2 d2 = abs(sz - uSafeCenter) - uSafeHalfSize;
    float dist = length(max(d2, 0.0)) + min(max(d2.x, d2.y), 0.0);
    col *= mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, dist));
    col = mix(col, vec3(${VDJ_DROP_FLASH.toFixed(2)}), uFlash);
    gl_FragColor = vec4(col, 1.0);
  }
`;

// Compiled once per (steps, neons) and never released (see
// pi-shader-compile-cost); the uniform objects are cached with it.
const MARCH = new Map();

function marchMaterial(steps, neons, stride) {
  const key = steps + 'x' + neons + 'x' + stride;
  if (MARCH.has(key)) return MARCH.get(key);
  const arr = (n, f) => { const a = []; for (let i = 0; i < n; i++) a.push(f()); return a; };
  const uniforms = {
    uAspect: { value: 16 / 9 },
    uCamPos: { value: new THREE.Vector3() },
    uCamCell: { value: new THREE.Vector3() },
    uCamRot: { value: new THREE.Matrix3() },
    uTanHalf: { value: Math.tan(FOV_DEG * Math.PI / 360) },
    uRoll: { value: 0 },
    uL: { value: L0 },
    uFly: { value: 0 },
    uNeonType: { value: arr(neons, () => 0) },
    uNeonPos: { value: arr(neons, () => new THREE.Vector3()) },
    uNeonRot: { value: arr(neons, () => new THREE.Matrix3()) },
    uNeonRad: { value: arr(neons, () => L0) }
  };
  const material = new THREE.ShaderMaterial({
    uniforms, vertexShader: quadVertexShader, fragmentShader: marchFragmentShader(steps, neons, stride),
    depthTest: false, depthWrite: false
  });
  const entry = { material, uniforms };
  MARCH.set(key, entry);
  return entry;
}

export class InfinityVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : 1;     // tier 0 is unavailable: run as 1
    this._layout = TIER_LAYOUT[this._tier];
    const fl = parseFloat(urlParam('infl'));
    this._forcedL = Number.isFinite(fl) && fl > 0.1 ? fl : null;
    const cam = urlParam('infcam');
    this._forcedCam = cam === 'fly' || cam === 'drift' ? cam : null;
    const st = parseInt(urlParam('infsteps'), 10);
    this._steps = Number.isFinite(st) && st >= 4 && st <= 128 ? st : this._layout.steps;
    // ?infscale=<f>: the march target's fraction of the drawing buffer (bench).
    const sc = parseFloat(urlParam('infscale'));
    this._scale = Number.isFinite(sc) && sc >= 0.1 && sc <= 1 ? sc : this._layout.scale;
    const sd = parseFloat(urlParam('infstride'));
    this._stride = Number.isFinite(sd) && sd >= 0.5 && sd <= 4 ? sd : this._layout.stride;
    const mm = urlParam('infmirror');
    this._forcedMirror = mm != null && VDJ_MIRROR_NAMES[mm] !== undefined ? VDJ_MIRROR_NAMES[mm] : null;
    // ?infdist=1: the closest-approach distance in grey; ?infcore=1: core only.
    this._debug = urlParam('infdist') === '1' ? 1 : (urlParam('infcore') === '1' ? 2 : 0);
    this._debugDrop = urlParam('infdrop') === '1';
    this._probe = urlParam('infprobe') === '1';
    // The halo stays under the bloom threshold: the bloom only adds a rim.
    this._post = new VdjPost({ bloomStrength: 0.6, bloomThreshold: 0.78 });
    this._comfort = new VdjComfort();
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const N = this._layout.neons;

    const march = marchMaterial(this._steps, N, this._stride);
    this._mu = march.uniforms;
    this._marchScene = new THREE.Scene();
    this._marchQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), march.material);
    this._marchQuad.frustumCulled = false;
    this._marchScene.add(this._marchQuad);
    this._ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    // HalfFloat: renderable with EXT_color_buffer_float (the Pi has it) and,
    // unlike 32-bit float, filterable in WebGL2 - the distances need both.
    this._target = new THREE.WebGLRenderTarget(4, 4, {
      minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, format: THREE.RGBAFormat,
      type: THREE.HalfFloatType, depthBuffer: false, stencilBuffer: false, generateMipmaps: false
    });

    const arr = (n, f) => { const a = []; for (let i = 0; i < n; i++) a.push(f()); return a; };
    this._cu = {
      uMarch: { value: this._target.texture }, uMarchSize: { value: new THREE.Vector2(4, 4) },
      uL: { value: L0 }, uPxAngle: { value: 0.001 }, uScreenPerTexel: { value: 2 },
      uNeonCol: { value: arr(NEON_SLOTS + 1, () => new THREE.Color()) },
      uNeonGain: { value: arr(NEON_SLOTS + 1, () => 1) },
      uAccent: { value: new THREE.Color(0.3, 0.2, 1.0) },
      uHaloPx: { value: HALO_PX }, uMirror: { value: 0 }, uGain: { value: 1 }, uFlash: { value: 0 },
      uDebug: { value: this._debug },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._compMaterial = new THREE.ShaderMaterial({
      uniforms: this._cu, vertexShader: compositeVertexShader, fragmentShader: compositeFragmentShader,
      depthTest: false, depthWrite: false
    });
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this._compMaterial);
    this._display.frustumCulled = false;
    this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._runPasses(renderer); };
    scene.add(this._display);

    // The comfort measurement: the SAME composite, rendered small and read
    // back every LUM_EVERY frames (what the room sees, before the bloom).
    this._lumTarget = new THREE.WebGLRenderTarget(LUM_W, LUM_H, {
      minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType, depthBuffer: false, stencilBuffer: false, generateMipmaps: false
    });
    this._lumScene = new THREE.Scene();
    this._lumQuad = new THREE.Mesh(this._display.geometry, this._compMaterial);
    this._lumQuad.frustumCulled = false;
    this._lumScene.add(this._lumQuad);
    this._lumPixels = new Uint8Array(LUM_W * LUM_H * 4);
    this._frame = 0;
    this._lastLumAt = 0;

    // --- state ---
    this._time = 0;
    this._paused = false;
    this._beatSeconds = 60 / FREE_BPM;
    this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrase = null; this._lastMode = null;
    this._beatCount = 0; this._barCount = 0; this._freeBeatAt = 0;
    this._sinceBeat = 10; this._beatPhase = 0; this._barPhase = 0; this._beats = 0;
    this._hexes = ['#30e0ff', '#ff3090', '#9040ff', '#40ff90'];
    this._colorCursor = 0;
    this._neons = [];
    this._L = this._forcedL || L0;
    this._buildAmt = 0;
    this._fly = this._forcedCam === 'fly';
    this._flyFrom = new THREE.Vector3();
    this._flyAt = 0;
    this._flySpeed = FLY_BOXES_PER_BEAT;
    this._camHome = new THREE.Vector3(0, 0, 0);     // the centre of the box
    this._forward = new THREE.Vector3(0, 0, -1);
    this._viewIndex = 0;
    this._mirror = this._forcedMirror != null ? this._forcedMirror : 0;
    this._rotPeriod = ROT_PERIOD_BEATS;
    this._dropUntil = -1;
    this._lastDropAt = -99;
    this._debugDropAt = 0;
    this._flash = 0;
    this._saturation = 0.9;
    this._camPos = new THREE.Vector3();
    this._lastPalette = undefined;

    this._newConfiguration();
    this._writeUniforms(0.5, 0.5, 0.5);
  }

  // --- the choreography ----------------------------------------------------------

  // A new set of neons: at least one of each kind, each centred 0.62-0.82 L
  // from the centre of the box (where the camera is), turning about itself.
  _newConfiguration() {
    const N = this._layout.neons;
    const kinds = [0, 1, 2];
    for (let i = 3; i < N; i++) kinds.push(Math.floor(Math.random() * 3));
    for (let i = kinds.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); const t = kinds[i]; kinds[i] = kinds[j]; kinds[j] = t; }
    this._neons = [];
    for (let i = 0; i < N; i++) {
      const axis = new THREE.Vector3(Math.random() * 2 - 1, Math.random() * 2 - 1, Math.random() * 2 - 1);
      if (axis.lengthSq() < 0.05) axis.set(0, 1, 0);
      axis.normalize();
      const tilt = new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.random() * 6.28, Math.random() * 6.28, Math.random() * 6.28));
      const dir = new THREE.Vector3(Math.random() * 2 - 1, Math.random() * 2 - 1, Math.random() * 2 - 1);
      if (dir.lengthSq() < 0.05) dir.set(1, 0, 0);
      dir.normalize();
      // Spread the directions: the n-th neon is pushed off the others.
      for (let k = 0; k < this._neons.length; k++) {
        const o = this._neons[k].pos.clone().normalize();
        if (dir.dot(o) > 0.6) dir.addScaledVector(o, -0.8).normalize();
      }
      const r = NEON_DIST_MIN + Math.random() * (NEON_DIST_MAX - NEON_DIST_MIN);
      this._neons.push({
        type: kinds[i],
        pos: dir.multiplyScalar(r),
        axis, tilt,
        phase: Math.floor(Math.random() * 4) / 4,      // quarter-turn offsets keep the 16-beat loop
        color: (this._colorCursor + i) % this._hexes.length
      });
    }
    this._colorCursor = (this._colorCursor + 1) % this._hexes.length;
  }

  // A camera cut: a new orientation, and on some phrases the FLY mode.
  _cutCamera(forceFly, speed) {
    let vi = this._viewIndex;
    while (vi === this._viewIndex && VIEWS.length > 1) vi = Math.floor(Math.random() * VIEWS.length);
    this._viewIndex = vi;
    const v = VIEWS[vi];
    this._forward.set(v[0], v[1], v[2]).normalize();
    const fly = this._forcedCam ? this._forcedCam === 'fly' : (forceFly || Math.random() < FLY_CHANCE);
    if (fly) {
      this._fly = true;
      this._flyFrom.copy(this._camHome).multiplyScalar(this._L);
      this._flyAt = this._beats;
      this._flySpeed = speed || FLY_BOXES_PER_BEAT;
    } else {
      this._fly = false;
    }
  }

  _onBar() {
    // One neon changes colour, in a cut.
    if (this._neons.length) {
      const n = this._neons[this._barCount % this._neons.length];
      n.color = (n.color + 1 + Math.floor(Math.random() * Math.max(1, this._hexes.length - 1))) % this._hexes.length;
    }
  }

  _onPhrase() {
    this._newConfiguration();
    this._cutCamera(false);
    if (this._forcedMirror == null) {
      this._mirror = VDJ_MIRROR_NAMES[MIRROR_MODES[Math.floor(Math.random() * MIRROR_MODES.length)]];
    }
  }

  _onDrop() {
    if (this._comfort.requestFlash(this._time)) this._flash = 1;
    this._dropUntil = this._beats + DROP_BARS * 4;
    this._saturation = 1.0;
    this._buildAmt = 0;
    this._cutCamera(true, DROP_FLY_BOXES_PER_BEAT);
  }

  // --- palette ------------------------------------------------------------------------

  _applyPalette(palette) {
    const vivid = (palette && palette.vividColors && palette.vividColors.length >= 2) ? palette.vividColors : null;
    this._hexes = vivid ? vivid.slice(0, 6) : ['#30e0ff', '#ff3090', '#9040ff', '#40ff90'];
  }

  onTrackChange(profile) { this._applyPalette(profile && profile.artworkPalette); }

  // --- the frame -----------------------------------------------------------------------

  update(deltaTime, profile) {
    const director = profile.director || {};
    if (profile.artworkPalette !== this._lastPalette) { this._lastPalette = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) { this._post.update(1); return; }   // frozen: the target is not re-rendered

    const bpmRaw = profile.trackFeatures && profile.trackFeatures.bpm;
    const bpm = bpmRaw > 0 ? THREE.MathUtils.clamp(bpmRaw, 40, 220) : FREE_BPM;
    this._beatSeconds = 60 / bpm;
    const beat = this._beatSeconds;
    const measured = !!director.hasReliableGrid;
    const bands = (measured && director.bands) || null;
    const low = bands ? (bands.low || 0) : 0.5;
    const mid = bands ? (bands.mid || 0) : 0.5;
    const high = bands ? (bands.high || 0) : 0.5;
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    this._sinceBeat += dt;
    const t = this._time;

    // --- the clocks ---
    let beatEdge = false, barEdge = false, phraseEdge = false;
    if (profile.beatPhase != null) {
      if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase) beatEdge = true;
      this._lastBeatPhase = profile.beatPhase;
    } else if (t - this._freeBeatAt >= beat) { this._freeBeatAt = t; beatEdge = true; }
    if (beatEdge) { this._beatCount++; this._sinceBeat = 0; }
    this._beatPhase = profile.beatPhase != null ? profile.beatPhase : Math.min(1, (t - this._freeBeatAt) / beat);
    this._beats = this._beatCount + this._beatPhase;
    if (profile.barPhase != null) {
      if (this._lastBarPhase != null && profile.barPhase < this._lastBarPhase) barEdge = true;
      this._lastBarPhase = profile.barPhase;
      this._barPhase = profile.barPhase;
    } else {
      if (beatEdge && this._beatCount % 4 === 0) barEdge = true;
      this._barPhase = ((this._beatCount % 4) + this._beatPhase) / 4;
    }
    if (barEdge) {
      this._barCount++;
      this._onBar();
      // bands.mid picks the neons' loop on the bar: 16 or 8 beats a turn.
      this._rotPeriod = mid > 0.62 ? 8 : 16;
    }
    const phrase = profile.phraseIndex;
    if (phrase != null && this._lastPhrase != null && phrase !== this._lastPhrase) phraseEdge = true;
    if (phrase == null && beatEdge && this._beatCount % 16 === 0) phraseEdge = true;
    this._lastPhrase = phrase;

    // --- drop, build ---
    let dropNow = false;
    if (measured && director.mode === 'drop' && this._lastMode !== 'drop') dropNow = true;
    const building = director.mode === 'build';
    this._lastMode = director.mode;
    if (this._debugDrop && t >= this._debugDropAt) { dropNow = true; this._debugDropAt = t + 8 * 4 * beat; }
    if (dropNow && this._beats - this._lastDropAt > 8) { this._lastDropAt = this._beats; this._onDrop(); }
    else if (phraseEdge && this._beats >= this._dropUntil) this._onPhrase();
    if (this._dropUntil > 0 && this._beats >= this._dropUntil && barEdge) {
      // The drop's four bars are over: back to the regular look, in a cut.
      this._dropUntil = -1;
      this._saturation = 0.9;
      this._cutCamera(false);
    }
    // A build tightens the box progressively; anything else lets it out.
    const bTarget = building ? 1 : 0;
    this._buildAmt += (bTarget - this._buildAmt) * Math.min(1, dt / (BUILD_EASE_BARS * 4 * beat) * 3);
    this._L = this._forcedL || L0 * (1 - (1 - BUILD_L) * this._buildAmt);

    this._writeUniforms(low, mid, high);
    if (this._probe && typeof window !== 'undefined') window.__infinity = this.getProbe();
  }

  _writeUniforms(low, mid, high) {
    const mu = this._mu, cu = this._cu;
    const L = this._L;
    mu.uL.value = L;
    cu.uL.value = L;
    cu.uHaloPx.value = HALO_PX * (0.8 + 0.5 * low);    // bands.low: the halo's width

    // --- the neons: a revolution in 16 (or 8) beats, exactly ---
    const pulse = Math.exp(-this._sinceBeat / PULSE_TAU_S);
    for (let i = 0; i < this._neons.length; i++) {
      const n = this._neons[i];
      const ang = 2 * Math.PI * (vdjLoop(this._beats, this._rotPeriod) + n.phase);
      _q.setFromAxisAngle(n.axis, ang).multiply(n.tilt);
      _m4.makeRotationFromQuaternion(_q);
      mu.uNeonRot.value[i].setFromMatrix4(_m4);
      mu.uNeonPos.value[i].copy(n.pos).multiplyScalar(L);
      mu.uNeonType.value[i] = n.type;
      mu.uNeonRad.value[i] = (n.type === 0 ? BAR_HALF : n.type === 1 ? RING_R : OCTA_R) * L;
      vdjColor(this._hexes[n.color % this._hexes.length], cu.uNeonCol.value[i], this._saturation);
      const flick = 1 + FLICKER * high * Math.sin(this._time * 31.0 + i * 7.3) * Math.sin(this._time * 17.0 + i * 3.1);
      cu.uNeonGain.value[i] = (1 + PULSE * pulse) * flick;
    }
    cu.uNeonGain.value[EDGE_INDEX] = 1 + PULSE * pulse;
    vdjColor(this._hexes[(this._colorCursor + 2) % this._hexes.length], cu.uAccent.value, this._saturation);

    // --- the camera: at the centre, drifting on a 16-beat loop, or flying ---
    const u = vdjLoop(this._beats, DRIFT_PERIOD_BEATS) * 2 * Math.PI;
    _v0.copy(this._forward);
    const upRef = Math.abs(_v0.y) > 0.95 ? new THREE.Vector3(0, 0, 1) : _up;
    _v1.crossVectors(_v0, upRef).normalize();
    const fwd = _v0.clone().applyAxisAngle(upRef, DRIFT_YAW * Math.sin(u)).applyAxisAngle(_v1, DRIFT_PITCH * Math.sin(2 * u)).normalize();
    if (this._fly) {
      const travel = (this._beats - this._flyAt) * this._flySpeed * 2 * L;
      this._camPos.copy(this._flyFrom).addScaledVector(this._forward, travel);
      fwd.copy(this._forward);
    } else {
      this._camPos.copy(this._camHome).multiplyScalar(L);
    }
    const kick = vdjCameraKick(this._barPhase, this._barCount, 4);
    _m4.lookAt(_v0.set(0, 0, 0), fwd, Math.abs(fwd.y) > 0.95 ? new THREE.Vector3(0, 0, 1) : _up);
    mu.uCamRot.value.setFromMatrix4(_m4);
    mu.uCamPos.value.copy(this._camPos);
    mu.uCamCell.value.set(
      Math.floor((this._camPos.x + L) / (2 * L)),
      Math.floor((this._camPos.y + L) / (2 * L)),
      Math.floor((this._camPos.z + L) / (2 * L)));
    mu.uFly.value = this._fly ? 1 : 0;
    const tanHalf = Math.tan(FOV_DEG * Math.PI / 360) / kick.zoom;
    mu.uTanHalf.value = tanHalf;
    mu.uRoll.value = kick.roll;
    this._tanHalf = tanHalf;

    // --- the look: RGB split on the beat, symmetry, flash ---
    this._post.update(vdjBeatChroma(this._beatPhase, this._beatSeconds));
    cu.uMirror.value = this._mirror;
    cu.uFlash.value = this._flash ? 1 : 0;
    this._flash = 0;
  }

  _runPasses(renderer) {
    renderer.getDrawingBufferSize(_size);
    const w = Math.max(16, Math.round(_size.x * this._scale));
    const h = Math.max(9, Math.round(_size.y * this._scale));
    if (this._target.width !== w || this._target.height !== h) this._target.setSize(w, h);
    this._mu.uAspect.value = w / h;
    this._cu.uMarchSize.value.set(w, h);
    this._cu.uScreenPerTexel.value = _size.y / h;
    // One screen pixel's world size at unit depth: the frame's height spans
    // 2 tan(fov / 2) at depth 1.
    this._cu.uPxAngle.value = 2 * (this._tanHalf || Math.tan(FOV_DEG * Math.PI / 360)) / _size.y;
    if (this._paused) return;
    const prev = renderer.getRenderTarget();
    const prevAuto = renderer.autoClear;
    renderer.autoClear = false;
    renderer.setRenderTarget(this._target);
    renderer.render(this._marchScene, this._ortho);
    this._frame++;
    if (this._frame % LUM_EVERY === 0) {
      // The flash is a one-frame event: not part of the running average.
      const flash = this._cu.uFlash.value;
      this._cu.uFlash.value = 0;
      renderer.setRenderTarget(this._lumTarget);
      renderer.render(this._lumScene, this._ortho);
      renderer.readRenderTargetPixels(this._lumTarget, 0, 0, LUM_W, LUM_H, this._lumPixels);
      this._cu.uFlash.value = flash;
      let s = 0;
      const px = this._lumPixels;
      for (let i = 0; i < LUM_W * LUM_H; i++) {
        const l = (0.2126 * px[i * 4] + 0.7152 * px[i * 4 + 1] + 0.0722 * px[i * 4 + 2]) / 255;
        s += Math.pow(l, 1 / 2.2);               // the chain sRGB-encodes the linear output
      }
      const dtS = this._time - this._lastLumAt;
      this._lastLumAt = this._time;
      this._cu.uGain.value = this._comfort.measure(s / (LUM_W * LUM_H), Math.max(0.05, dtS));
    }
    renderer.setRenderTarget(prev);
    renderer.autoClear = prevAuto;
  }

  getProbe() {
    const mu = this._mu;
    return {
      t: this._time, beats: this._beats, bar: this._barCount, L: this._L, fly: this._fly,
      camPos: this._camPos.toArray(), camCell: mu.uCamCell.value.toArray(), forward: this._forward.toArray(),
      mirror: this._mirror, rotPeriod: this._rotPeriod, drop: this._dropUntil > 0,
      neons: this._neons.map((n) => n.type), steps: this._steps, scale: this._scale,
      target: [this._target.width, this._target.height],
      gain: this._cu.uGain.value, meanLum: this._comfort.average,
      aberration: this._post.value.aberration, pxAngle: this._cu.uPxAngle.value
    };
  }

  dispose() {
    this._display.onBeforeRender = null;
    this._scene.remove(this._display);
    this._display.geometry.dispose();
    this._compMaterial.dispose();
    this._marchQuad.geometry.dispose();     // the march material is cached - see marchMaterial
    this._target.dispose();
    this._lumTarget.dispose();
    if (typeof window !== 'undefined' && window.__infinity) delete window.__infinity;
    this._scene = null;
  }
}
